/**
 * Vendor Dispatches Inbox
 * ------------------------
 * Route: /vendor/dispatches
 *
 * Reads program-launch dispatches routed to the authenticated vendor
 * (via GET /api/vendor/dispatches) and lets them progress each one
 * through the status pipeline:
 *
 *    pending -> accepted -> in_progress -> completed
 *                                       -> cancelled
 *
 * Each transition POSTs to /api/vendor/dispatches/{launch_id}/status
 * and updates the row inline.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Inbox, RefreshCw, Loader2, CheckCircle2, XCircle, Play, ArrowRight, Mail, Phone, Calendar, User } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { devWarn } from "@/lib/devlog";

const TABS = [
  { id: "active",    label: "Active",     help: "Everything not yet completed or cancelled" },
  { id: "pending",   label: "Pending",    help: "Waiting for you to accept" },
  { id: "accepted",  label: "Accepted",   help: "Accepted, not yet started" },
  { id: "in_progress", label: "In progress", help: "You've started service delivery" },
  { id: "completed", label: "Completed",  help: "Service delivered" },
  { id: "cancelled", label: "Cancelled",  help: "Cancelled by you or the clinic" },
];

const STATUS_STYLE = {
  pending:      { bg: "bg-amber-50",   ring: "ring-amber-200",   text: "text-amber-800",   dot: "bg-amber-500"   },
  accepted:     { bg: "bg-blue-50",    ring: "ring-blue-200",    text: "text-blue-800",    dot: "bg-blue-500"    },
  in_progress:  { bg: "bg-violet-50",  ring: "ring-violet-200",  text: "text-violet-800",  dot: "bg-violet-500"  },
  completed:    { bg: "bg-emerald-50", ring: "ring-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
  cancelled:    { bg: "bg-slate-50",   ring: "ring-slate-200",   text: "text-slate-600",   dot: "bg-slate-400"   },
  email_failed: { bg: "bg-rose-50",    ring: "ring-rose-200",    text: "text-rose-800",    dot: "bg-rose-500"    },
};

const STATUS_LABEL = {
  pending: "Pending",
  accepted: "Accepted",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  email_failed: "Email failed",
};

// Which action buttons render for a given current status.
const NEXT_ACTIONS = {
  pending:     [{ to: "accepted",    label: "Accept",      icon: CheckCircle2, variant: "primary" },
                { to: "cancelled",   label: "Decline",     icon: XCircle,      variant: "ghost"   }],
  accepted:    [{ to: "in_progress", label: "Start work",  icon: Play,         variant: "primary" },
                { to: "cancelled",   label: "Cancel",      icon: XCircle,      variant: "ghost"   }],
  in_progress: [{ to: "completed",   label: "Mark complete", icon: CheckCircle2, variant: "primary" },
                { to: "cancelled",   label: "Cancel",      icon: XCircle,      variant: "ghost"   }],
  completed:   [],
  cancelled:   [],
  email_failed: [],
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span
      data-testid={`dispatch-status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${s.bg} ${s.ring} ${s.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function DueBy({ complete_by }) {
  if (!complete_by) return null;
  const due = new Date(complete_by);
  const now = new Date();
  const diffDays = Math.round((due - now) / 86400000);
  const tone =
    diffDays < 0 ? "text-rose-700"
    : diffDays <= 1 ? "text-amber-700"
    : "text-slate-600";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${tone}`}>
      <Calendar className="w-3 h-3" />
      Due {due.toLocaleDateString()} {diffDays < 0 ? "(overdue)" : diffDays === 0 ? "(today)" : `(in ${diffDays}d)`}
    </span>
  );
}

function DispatchRow({ row, onTransition, busyId }) {
  const isBusy = busyId === row.dispatch_id;
  const actions = NEXT_ACTIONS[row.status] || [];
  const consumer = row.consumer || {};
  const consumerName = `${consumer.first_name || ""} ${consumer.last_name || ""}`.trim() || "Patient";

  return (
    <div
      data-testid={`dispatch-row-${row.dispatch_id}`}
      className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-sm transition-shadow"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-400">
              {row.program_category || "Program"}
            </span>
            <span className="text-slate-300">·</span>
            <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-400">
              {row.category || "Service"}
            </span>
          </div>
          <h3 className="mt-1 font-display text-lg font-bold text-slate-900 leading-tight">
            {row.program_name || "Program"} — from {row.clinic_name || "Clinic"}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-700">
            <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5 text-slate-400" />{consumerName}</span>
            {consumer.email && (
              <a href={`mailto:${consumer.email}`} className="inline-flex items-center gap-1 text-blue-700 hover:underline">
                <Mail className="w-3.5 h-3.5" />{consumer.email}
              </a>
            )}
            {consumer.phone && (
              <a href={`tel:${consumer.phone}`} className="inline-flex items-center gap-1 text-blue-700 hover:underline">
                <Phone className="w-3.5 h-3.5" />{consumer.phone}
              </a>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <DueBy complete_by={row.complete_by} />
            {row.reminder_sent_at && (
              <span className="text-[11px] text-slate-500 italic">Reminder sent {new Date(row.reminder_sent_at).toLocaleString()}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <StatusPill status={row.status} />
          {row.status_updated_at && (
            <span className="text-[10.5px] text-slate-400">
              Updated {new Date(row.status_updated_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {actions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
          {actions.map((a) => {
            const Icon = a.icon;
            const base = "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50";
            const style = a.variant === "primary"
              ? "bg-slate-900 hover:bg-slate-800 text-white"
              : "border border-slate-200 hover:bg-slate-50 text-slate-700";
            return (
              <button
                key={a.to}
                type="button"
                disabled={isBusy}
                onClick={() => onTransition(row, a.to)}
                data-testid={`dispatch-action-${a.to}-${row.dispatch_id}`}
                className={`${base} ${style}`}
              >
                {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                {a.label}
                {a.variant === "primary" && <ArrowRight className="w-3 h-3" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function VendorDispatches() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (activeTab) => {
    setLoading(true);
    try {
      const r = await api.get("/vendor/dispatches", { params: { status: activeTab } });
      setRows(r.data?.dispatches || []);
      setSummary(r.data?.summary || {});
    } catch (e) {
      devWarn("[VendorDispatches] load failed:", e);
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [load, tab]);

  const transition = async (row, nextStatus) => {
    setBusyId(row.dispatch_id);
    try {
      await api.post(`/vendor/dispatches/${row.launch_id}/status`, { status: nextStatus });
      toast.success(`Marked ${STATUS_LABEL[nextStatus].toLowerCase()}`);
      // Refresh
      await load(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const tabCounts = useMemo(() => {
    const activeCount =
      (summary.pending || 0) +
      (summary.accepted || 0) +
      (summary.in_progress || 0) +
      (summary.email_failed || 0);
    return {
      active: activeCount,
      pending: summary.pending || 0,
      accepted: summary.accepted || 0,
      in_progress: summary.in_progress || 0,
      completed: summary.completed || 0,
      cancelled: summary.cancelled || 0,
    };
  }, [summary]);

  return (
    <div data-testid="vendor-dispatches-page" className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-blue-700 mb-1">
            Vendor · Program dispatches
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
            Dispatches
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Every service request a clinic has dispatched to you through the Nodera network.
            Accept, start work, and mark complete so the clinic sees status updates in real time.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(tab)}
          disabled={loading}
          data-testid="vendor-dispatches-refresh"
          className="inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 rounded-full px-3.5 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 mb-6 border-b border-slate-200 pb-3">
        {TABS.map((t) => {
          const active = tab === t.id;
          const count = tabCounts[t.id];
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`vendor-dispatches-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                active
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              title={t.help}
            >
              {t.label}
              <span className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold ${
                active ? "bg-white/20 text-white" : "bg-white text-slate-600"
              }`}>
                {count || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Rows */}
      {loading && rows.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading dispatches…
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="vendor-dispatches-empty"
          className="text-center py-16 border border-dashed border-slate-200 rounded-2xl"
        >
          <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-semibold text-slate-700">No {tab === "active" ? "active " : `${tab.replace("_", " ")} `}dispatches</div>
          <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
            When a clinic launches a program with your services included, the dispatch will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <DispatchRow
              key={r.dispatch_id || `${r.launch_id}:${r.vendor_id}`}
              row={r}
              onTransition={transition}
              busyId={busyId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
